import { PROFILE_SCHEMA } from 'app/api/auth/[...nextauth]/schema';
import type { schema } from 'database';
import { z } from 'zod';

export type CommentDocument = schema.CommentDocument;
export type CommentNode = schema.CommentNode;
export const EMPTY_COMMENT: CommentDocument = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

const blocks = new Set([
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'hardBreak',
  'horizontalRule',
  'text',
]);
const marks = new Set([
  'bold',
  'italic',
  'underline',
  'strike',
  'code',
  'link',
]);

export function safeCommentLink(value: unknown): string | undefined {
  if (typeof value !== 'string' || value.length > 2048) return;
  try {
    const url = new URL(value);
    if (['https:', 'http:', 'mailto:'].includes(url.protocol)) return url.href;
  } catch {
    /* An invalid URL is not rendered as a link. */
  }
}

// Validate JSON on the server, and render it as React nodes. Never execute MDX
// or accept HTML, scripts, images, arbitrary attributes, or embedded content.
export function normalizeCommentDocument(value: unknown): CommentDocument {
  let nodeCount = 0;
  let textLength = 0;
  const walk = (value: unknown, depth: number): CommentNode => {
    if (++nodeCount > 1000 || depth > 12 || !value || typeof value !== 'object')
      throw new Error('Invalid comment content');
    const node = value as CommentNode;
    if (!blocks.has(node.type))
      throw new Error('Unsupported comment formatting');
    const result: CommentNode = { type: node.type };
    if (node.type === 'text') {
      if (typeof node.text !== 'string')
        throw new Error('Invalid comment text');
      result.text = node.text;
      textLength += node.text.length;
      if (node.marks) {
        if (!Array.isArray(node.marks) || node.marks.length > marks.size)
          throw new Error('Invalid comment formatting');
        result.marks = node.marks.map((mark) => {
          if (!marks.has(mark.type))
            throw new Error('Unsupported comment formatting');
          if (mark.type !== 'link') return { type: mark.type };
          const href = safeCommentLink(mark.attrs?.href);
          if (!href) throw new Error('Invalid comment link');
          return { type: 'link', attrs: { href } };
        });
      }
    }
    if (node.type === 'heading')
      result.attrs = {
        level: Math.max(1, Math.min(6, Number(node.attrs?.level) || 2)),
      };
    if (node.type === 'orderedList')
      result.attrs = {
        start: Math.max(1, Math.min(1000, Number(node.attrs?.start) || 1)),
      };
    if (node.content !== undefined) {
      if (!Array.isArray(node.content))
        throw new Error('Invalid comment content');
      result.content = node.content.map((child) => walk(child, depth + 1));
    }
    return result;
  };
  const document = value as CommentDocument | undefined;
  if (document?.type !== 'doc' || !Array.isArray(document.content))
    throw new Error('Invalid comment document');
  const content = document.content.map((node) => walk(node, 0));
  if (
    !textLength ||
    textLength > 10000 ||
    !commentText({ type: 'doc', content }).trim()
  )
    throw new Error('Comments must contain 1–10,000 characters');
  return { type: 'doc', content };
}

export function commentText(document: CommentDocument | null): string {
  const text = (node: CommentNode): string =>
    node.text ??
    (node.content ?? []).map(text).join(node.type === 'paragraph' ? '' : '\n');
  return document?.content.map(text).join('\n') ?? '';
}

export const commentDocumentSchema = z.unknown().transform((value, context) => {
  try {
    return normalizeCommentDocument(value);
  } catch (error) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: error instanceof Error ? error.message : 'Invalid comment',
    });
    return z.NEVER;
  }
});
const id = z.string().uuid();
// Existing posts also use short text IDs from the Apple Notes integration.
const postId = z.string().min(1).max(100);
const name = PROFILE_SCHEMA.shape.name.unwrap();
export const commentRequestSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('request-code'),
    postId,
    email: z
      .string()
      .trim()
      .email()
      .max(254)
      .transform((email) => email.toLowerCase()),
  }),
  z.object({
    action: z.literal('verify-code'),
    postId,
    email: z
      .string()
      .trim()
      .email()
      .max(254)
      .transform((email) => email.toLowerCase()),
    challengeId: id,
    code: z.string().regex(/^\d{6}$/),
  }),
  z.object({ action: z.literal('logout'), postId }),
  z.object({
    action: z.literal('profile'),
    postId,
    name,
    removeAvatar: z.boolean().optional(),
  }),
  z.object({
    action: z.literal('settings'),
    postId,
    replyNotifications: z.boolean(),
    likeNotifications: z.boolean(),
  }),
  z.object({
    action: z.literal('read-notifications'),
    postId,
    notificationId: id.optional(),
  }),
  z.object({
    action: z.literal('create'),
    postId,
    parentId: id.optional(),
    document: commentDocumentSchema,
  }),
  z.object({
    action: z.literal('edit'),
    postId,
    commentId: id,
    document: commentDocumentSchema,
  }),
  z.object({ action: z.literal('delete'), postId, commentId: id }),
  z.object({
    action: z.literal('pin'),
    postId,
    commentId: id,
    pinned: z.boolean(),
  }),
  z.object({
    action: z.literal('vote'),
    postId,
    commentId: id,
    liked: z.boolean(),
  }),
]);
export type CommentRequest = z.infer<typeof commentRequestSchema>;
export const commentListSchema = z.object({
  postId,
  page: z.coerce.number().int().min(0).max(10000).default(0),
  sort: z.enum(['newest', 'oldest', 'popular']).default('newest'),
  rootId: id.optional(),
});

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine(
    (value) =>
      !Number.isNaN(Date.parse(value)) &&
      new Date(value).toISOString().startsWith(value),
  );
export const myCommentsSchema = z
  .object({
    postId,
    scope: z.enum(['all', 'current']).default('all'),
    domain: z.string().trim().max(253).default(''),
    search: z.string().trim().max(100).default(''),
    from: date.optional(),
    to: date.optional(),
    sort: z.enum(['newest', 'oldest', 'domain']).default('newest'),
    page: z.coerce.number().int().min(0).max(10000).default(0),
  })
  .refine((value) => !value.from || !value.to || value.from <= value.to);
export type MyCommentsInput = z.infer<typeof myCommentsSchema>;
export const commentNotificationListSchema = z.object({
  postId,
  page: z.coerce.number().int().min(0).max(10000).default(0),
});
export type CommentNotificationList = {
  notifications: {
    id: string;
    kind: 'comment' | 'reply' | 'like';
    author: { name: string; avatar: string | null } | null;
    preview: string;
    postTitle: string | null;
    domain: string;
    url: string | null;
    createdAt: string;
    read: boolean;
  }[];
  unread: number;
  page: number;
  hasMore: boolean;
};
export type CommentViewer = {
  userId: string;
  name: string;
  email: string;
  avatar: string | null;
  verified: boolean;
  replyNotifications: boolean;
  likeNotifications: boolean;
};
export type MyComments = {
  comments: {
    id: string;
    document: CommentDocument | null;
    createdAt: string;
    postTitle: string | null;
    domain: string;
    url: string | null;
    reply: boolean;
  }[];
  total: number;
  pageSize: number;
  page: number;
};

export type PublicComment = {
  id: string;
  parentId: string | null;
  rootId: string | null;
  author: { name: string; avatar: string | null; isOwner: boolean };
  document: CommentDocument | null;
  deleted: boolean;
  pinnedAt: string | null;
  hasPinnedReplies: boolean;
  createdAt: string;
  updatedAt: string | null;
  likes: number;
  liked: boolean;
  canEdit: boolean;
  canDelete: boolean;
  replyCount: number;
};
export type CommentList = {
  comments: PublicComment[];
  count: number;
  hasMore: boolean;
  viewer: CommentViewer | null;
  canModerate: boolean;
};
